import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Flame, Search, Repeat, ShieldCheck, Building2 } from "lucide-react";
import { SiteShell } from "@/components/site-shell";
import { Badge } from "@/components/cards";
import { formatINR, initiatives } from "@/data/sample";
import {
  campaigns,
  campaignPct,
  daysLeft,
  getPartner,
  domainName,
  programs,
  type Campaign,
} from "@/data/network";

// Campaigns run directly by the foundation itself vs. campaigns raised by a
// partner institution (subject to admin approval before going live).
const MANAVTA_ORG_ID = "manavta-foundation";

// Institute campaigns hidden from the public listing (withdrawn / no longer featured).
const HIDDEN_INSTITUTE_CAMPAIGN_IDS = [
  "monthly-fodder-dharampur-1000", // Monthly Fodder for 1,000 Gir Cows — Dharampur Gaushala
  "ambulance-fuel-pfa", // Ambulance Fuel Support — PFA Gandhinagar
  "school-kits-2000-children", // School kits for 2,000 migrant workers' children
  "coastal-plantation-25000-trees", // 25,000 native trees along the Bhavnagar coast
  "tailoring-units-saurashtra", // 40 tailoring units for women's self-help groups
];

export const Route = createFileRoute("/campaigns/")({
  head: () => ({
    meta: [
      { title: "Live Campaigns — Manavta" },
      {
        name: "description",
        content:
          "Support live, verified fundraising campaigns from panjrapols, gaushalas and NGOs across every Manavta giving domain.",
      },
      { property: "og:title", content: "Live Campaigns — Manavta" },
      {
        property: "og:description",
        content:
          "Verified campaigns across animal welfare, education, healthcare, food and environment.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CampaignsIndex,
});

function CampaignsIndex() {
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("all");
  const [tab, setTab] = useState<"campaign" | "standard">("campaign");

  const list = useMemo(
    () =>
      campaigns.filter(
        (c) =>
          c.status !== "Closed" &&
          (domain === "all" || c.domain === domain) &&
          (q === "" || c.title.toLowerCase().includes(q.toLowerCase())),
      ),
    [q, domain],
  );

  const manavtaCampaigns = list.filter((c) => c.orgId === MANAVTA_ORG_ID);
  const instituteCampaigns = list.filter(
    (c) => c.orgId !== MANAVTA_ORG_ID && !HIDDEN_INSTITUTE_CAMPAIGN_IDS.includes(c.id),
  );

  return (
    <SiteShell>
      <section className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">
            Give where it matters
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Campaigns & standard giving
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-foreground/70 sm:text-base">
            Time-bound campaigns raise for a specific goal. Standard giving keeps everyday seva
            running month after month.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setTab("campaign")}
            className={`rounded-full px-5 py-2 text-sm font-semibold ${tab === "campaign" ? "bg-[var(--maroon)] text-[var(--cream)]" : "border border-border"}`}
          >
            Campaigns
          </button>
          <button
            onClick={() => setTab("standard")}
            className={`rounded-full px-5 py-2 text-sm font-semibold ${tab === "standard" ? "bg-[var(--maroon)] text-[var(--cream)]" : "border border-border"}`}
          >
            Standard monthly giving
          </button>
        </div>

        {tab === "campaign" ? (
          <>
            <div className="mt-6 grid gap-3 rounded-2xl border border-border/60 bg-card p-4 sm:grid-cols-2">
              <label className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
                <Search className="h-4 w-4 text-foreground/70" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search campaigns"
                  className="w-full bg-transparent text-sm outline-none"
                />
              </label>
              <select
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="all">All giving domains</option>
                {initiatives.map((i) => (
                  <option key={i.slug} value={i.slug}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Manavta Campaigns — run directly by the foundation */}
            {manavtaCampaigns.length > 0 && (
              <div className="mt-8">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--maroon)] text-white">
                    <ShieldCheck className="h-4.5 w-4.5" />
                  </div>
                  <h2 className="font-display text-lg font-bold text-[var(--maroon-deep)]">
                    Manavta Campaigns
                  </h2>
                </div>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {manavtaCampaigns.map((c) => (
                    <CampaignCard key={c.id} c={c} official />
                  ))}
                </div>
              </div>
            )}

            {/* Institute Campaigns — raised by a partner institution, admin-approved */}
            {instituteCampaigns.length > 0 && (
              <div className="mt-10">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--cream-2)] text-[var(--maroon)]">
                    <Building2 className="h-4.5 w-4.5" />
                  </div>
                  <h2 className="font-display text-lg font-bold text-[var(--maroon-deep)]">
                    Institute Campaigns
                  </h2>
                </div>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {instituteCampaigns.map((c) => (
                    <CampaignCard key={c.id} c={c} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((p) => {
              const org = getPartner(p.orgId);
              return (
                <div
                  key={p.id}
                  className="flex flex-col rounded-2xl border border-border/60 bg-card p-6"
                >
                  <Repeat className="h-5 w-5 text-[var(--saffron-deep)]" />
                  <p className="mt-3 text-xs text-foreground/70">
                    {org?.name} · {domainName(p.domain)}
                  </p>
                  <h2 className="mt-1 font-display text-lg font-semibold">{p.title}</h2>
                  <p className="mt-1 text-sm text-foreground/65">{p.impact}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-display text-xl font-bold text-[var(--maroon)]">
                      {formatINR(p.monthly)}
                      <span className="text-sm font-medium">/mo</span>
                    </span>
                    <Link
                      to="/donate"
                      className="rounded-full bg-[var(--maroon)] px-4 py-2 text-xs font-semibold text-[var(--cream)]"
                    >
                      Start monthly
                    </Link>
                  </div>
                  <p className="mt-2 text-xs text-foreground/70">
                    {p.subscribers.toLocaleString("en-IN")} monthly donors
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </SiteShell>
  );
}

function CampaignCard({ c, official = false }: { c: Campaign; official?: boolean }) {
  const org = getPartner(c.orgId);
  return (
    <Link
      to="/campaigns/$id"
      params={{ id: c.id }}
      className={`group flex flex-col overflow-hidden rounded-2xl border bg-card shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)] ${
        official ? "border-[var(--maroon)]/30 ring-1 ring-[var(--maroon)]/15" : "border-border/60"
      }`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={c.image}
          alt={c.title}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {c.urgency !== "Normal" && (
            <Badge tone={c.urgency === "Critical" ? "critical" : "urgent"}>
              <Flame className="h-3 w-3" /> {c.urgency}
            </Badge>
          )}
          <Badge tone="neutral">{domainName(c.domain)}</Badge>
        </div>
        {official && (
          <div className="absolute right-3 top-3">
            <Badge tone="featured">
              <ShieldCheck className="h-3 w-3" /> Manavta Official
            </Badge>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <p className="text-xs text-foreground/70">{org?.name}</p>
        <h2 className="font-display text-base font-semibold leading-snug">{c.title}</h2>
        <p className="line-clamp-2 text-sm text-foreground/65">{c.summary}</p>
        <div className="mt-auto pt-3">
          <div className="h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
            <div
              className="h-full"
              style={{ width: `${campaignPct(c)}%`, background: "var(--gradient-warm)" }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs">
            <span className="font-semibold text-[var(--maroon)]">{formatINR(c.raised)}</span>
            <span className="text-foreground/60">{daysLeft(c.endDate)} days left</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
